import { BehaviorSubject } from 'rxjs';
import config from 'config';
import { fetchWrapper, Role } from 'utils';
import { customerService } from './customer.service';
import { hasAccess } from '../utils';


const userSubject = new BehaviorSubject(null);
const authSubject = new BehaviorSubject(null);
const authUrl = `${config.apiUrl}/auth`;
const baseUrl = `${config.apiUrl}/account`;

export const accountService = {
  notify,
  login,
  logout,
  refreshToken,
  register,
  verifyEmail,
  sendEmail,
  forgotPassword,
  validateResetToken,
  resetPassword,
  getAll,
  getById,
  create,
  update,
  delete: _delete,
  user: userSubject.asObservable(),
  get userValue() {
    return userSubject.value;
  },
  auth: authSubject.asObservable(),
  get authValue() {
    return authSubject.value;
  },
};

function notify(params) {
  return fetchWrapper.post(`${baseUrl}/notify`, params);
}

async function login(username, password) {
  return fetchWrapper
    .post(`${authUrl}/login`, { username, password })
    .then(async (res) => {
      // publish user to subscribers and start timer to refresh token
      localStorage.setItem('refreshToken', res.refreshToken);
      authSubject.next(res);

      startRefreshTokenTimer();

      const user = await getMe();
      userSubject.next({ ...user, ...res });

      if (!hasAccess(user, Role.Editor)) {
        return customerService.login(user.customerId);
      }
      return user;
    })
    .catch((err) => console.error(err));
}

function logout() {
  // revoke token, stop refresh timer, publish null to user subscribers and redirect to login page
  fetchWrapper.post(`${authUrl}/logout`, {});
  stopRefreshTokenTimer();
  userSubject.next(null);
  authSubject.next(null);
  localStorage.removeItem('refreshToken');
  customerService.logout();
}

function refreshToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  return fetchWrapper
    .post(`${authUrl}/refresh`, { refreshToken })
    .then(async (token) => {
      localStorage.setItem('refreshToken', token.refreshToken);
      authSubject.next(token);
      const user = await getMe();
      userSubject.next({ ...user, ...token });
      startRefreshTokenTimer();
      if (!hasAccess(user, Role.Editor)) {
        return customerService.login(user.customerId);
      }
    })
    .catch((err) => console.log(err));
}

function getMe() {
  return fetchWrapper.get((`${baseUrl}/me`));
}

function register(params) {
  return fetchWrapper.post(`${baseUrl}/register`, params);
}

function verifyEmail(token) {
  return fetchWrapper.post(`${baseUrl}/verify-email`, { token });
}

function sendEmail(email) {
  return fetchWrapper.post(`${baseUrl}/send-email`, { email });
}

function forgotPassword(email) {
  return fetchWrapper.post(`${authUrl}/forgot-password`, { email });
}

function validateResetToken(token) {
  return fetchWrapper.post(`${authUrl}/validate-reset-token`, { token });
}

function resetPassword({ token, password }) {
  return fetchWrapper.post(`${authUrl}/reset-password`, {
    token,
    password,
  });
}

function getAll() {
  return fetchWrapper.get(baseUrl);
}

function getById(id) {
  return fetchWrapper.get(`${baseUrl}/${id}`);
}

function create(params) {
  return fetchWrapper.post(baseUrl, params);
}

function update(id, params) {
  return fetchWrapper.put(`${baseUrl}/${id}`, params).then((user) => {
    // update stored user if the logged in user updated their own record
    if (user.id === userSubject.value.id) {
      // publish updated user to subscribers
      user = { ...userSubject.value, ...user };
      userSubject.next(user);
    }
    return user;
  });
}

// prefixed with underscore because 'delete' is a reserved word in javascript
function _delete(id) {
  return fetchWrapper.delete(`${baseUrl}/${id}`).then((x) => {
    // auto logout if the logged in user deleted their own record
    if (id === userSubject.value.id) {
      logout();
    }
    return x;
  });
}

// helper functions

let refreshTokenTimeout;

function startRefreshTokenTimer() {
  // parse json object from base64 encoded jwt token
  const jwtToken = JSON.parse(atob(authSubject.value.accessToken.split('.')[1]));

  // set a timeout to refresh the token a minute before it expires
  const expires = new Date(jwtToken.exp * 1000);
  const timeout = expires.getTime() - Date.now() - 60 * 1000;
  refreshTokenTimeout = setTimeout(refreshToken, timeout);
}

function stopRefreshTokenTimer() {
  clearTimeout(refreshTokenTimeout);
}
